import { type ComputedRef, type Ref, ref, type WritableComputedRef } from 'vue'
import { getDocument } from '@/helpers/globals'

import type { UpdateSingleInputUiValue } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

export function useInputRangeTwoKnobbedMouse(
    valueMinUi: WritableComputedRef<number>,
    valueMaxUi: WritableComputedRef<number>,
    onChangeMin: () => void,
    onChangeMax: () => void,
    updateSingleInputUiValue: UpdateSingleInputUiValue,
    minFinal: ComputedRef<number>,
    step: Readonly<Ref<number>>,
    rangeClamped: ComputedRef<number>,
    disabled: Ref<boolean>
): {
    select: (event: PointerEvent & { currentTarget: HTMLElement }) => void
    indicatorElement: Ref<HTMLElement | null, HTMLElement | null>
} {
    const indicatorElement = ref<HTMLElement | null>(null)

    const document = getDocument()

    const select = (event: PointerEvent & { currentTarget: HTMLElement }) => {
        if (disabled.value) {
            return
        }

        const rangePercent =
            (event.offsetX / event.currentTarget.offsetWidth) * 100

        const valueAtClick = (rangeClamped.value * rangePercent) / 100

        // TODO account for 'any' step
        const numberOfSteps = Math.round(valueAtClick / Number(step.value))
        const valueStepped = numberOfSteps * Number(step.value) + minFinal.value

        const activeElement =
            Math.abs(valueStepped - valueMinUi.value) <
            Math.abs(valueStepped - valueMaxUi.value)
                ? 0
                : 1

        updateSingleInputUiValue(valueStepped, activeElement)

        const handleMouseUp = () => {
            if (!document) {
                return
            }

            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('mousemove', handleMouseMove)

            switch (activeElement) {
                case 0:
                    onChangeMin()
                    break
                case 1:
                    onChangeMax()
                    break
            }
        }

        if (document) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }
    }

    const handleMouseMove = (event: MouseEvent) => {
        const rect = indicatorElement.value?.getBoundingClientRect() || {
            left: 0,
            width: 0,
        }

        const mousePositionRelative = Math.max(
            0,
            Math.min(event.clientX - rect.left, rect.width)
        )

        const rangePercent = (mousePositionRelative / rect.width) * 100
        const valueAtMove = (rangeClamped.value * rangePercent) / 100
        const numberOfSteps = Math.round(valueAtMove / Number(step.value))
        const valueStepped = numberOfSteps * Number(step.value) + minFinal.value

        updateSingleInputUiValue(valueStepped)
    }

    return {
        select,
        indicatorElement,
    }
}
